import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  MdPerson,
  MdDirectionsCar,
  MdPhone,
  MdLocationOn,
  MdSearch,
  MdRefresh,
  MdAdd,
  MdEdit,
  MdVerified,
  MdSend,
  MdCheckCircle,
  MdGridView,
  MdViewList,
  MdRadar,
} from "react-icons/md";
import "./VendorCabDrivers.css";

/* ==================== CONFIG ==================== */
const API = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api/vendor/cabs`
  : "https://bmtvendorvendorfinal.onrender.com/api/vendor/cabs";

const PAGE_SIZE = 10;

const FILTER_PILLS = [
  { id: "all", label: "All Drivers" },
  { id: "assigned", label: "🟢 Assigned" },
  { id: "unassigned", label: "⚠️ Unassigned Cabs" },
  { id: "40km", label: "🎯 40km Hub Fleet" },
];

const EMPTY_FORM = {
  cabId: "",
  driverName: "",
  driverPhone: "",
  driverAlternatePhone: "",
  driverLicenseNumber: "",
  experienceYears: "",
  driverPhoto: "",
};

/* ==================== HELPERS ==================== */
const hasDriver = (d) => Boolean(d.driverName || d.driverPhone);
const digitsOnly = (v) => String(v || "").replace(/\D/g, "");
const errMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const normalizeCab = (cab) => ({
  _id: cab._id,
  cabId: cab._id,
  vehicleNumber: cab.vehicleNumber || "",
  cabType: cab.cabType || "Sedan",
  brand: cab.brand || "",
  model: cab.model || "",
  vehicleColor: cab.vehicleColor || "",
  baseLocation: cab.baseLocation || cab.pickupLocation || cab.fromCity || "Primary Hub",
  serviceRadiusKm: cab.serviceRadiusKm || 40,
  isActive: cab.isActive !== false,
  driverName: cab.driver?.name || cab.driverName || "",
  driverPhone: cab.driver?.phone || cab.driverPhone || "",
  driverAlternatePhone: cab.driver?.alternatePhone || cab.driverAlternatePhone || "",
  driverLicenseNumber: cab.driver?.licenseNumber || cab.driverLicenseNumber || "",
  experienceYears: cab.driver?.experienceYears || cab.driverExperience || 0,
  driverPhoto: cab.driver?.photo || cab.driverPhoto || "",
  createdAt: cab.createdAt,
});

function Field({ label, error, children }) {
  return (
    <div className={`vcd-form-group ${error ? "vcd-has-error" : ""}`}>
      <label>{label}</label>
      {children}
      {error && <small className="vcd-field-error">{error}</small>}
    </div>
  );
}

/* ==================== MAIN ==================== */
export default function VendorCabDrivers() {
  const token =
    localStorage.getItem("vendorToken") || localStorage.getItem("token");

  const [drivers, setDrivers] = useState([]);
  const [profileDriver, setProfileDriver] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all"); // all | assigned | unassigned | 40km
  const [viewMode, setViewMode] = useState("table"); // table | cards
  const [page, setPage] = useState(1);

  const [notice, setNotice] = useState(null); // { type, text }

  // Full page form
  const [showForm, setShowForm] = useState(false);
  const [editingExisting, setEditingExisting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const authConfig = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const showNotice = useCallback((type, text) => setNotice({ type, text }), []);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  /* ---------- FETCH ---------- */
  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      // 1. dedicated /drivers endpoint
      let loaded = false;
      try {
        const res = await axios.get(`${API}/drivers`, authConfig);
        if (res.data?.success !== false && Array.isArray(res.data?.data)) {
          setDrivers(
            res.data.data.map((d) => ({ ...d, cabId: d.cabId || d._id }))
          );
          setProfileDriver(res.data.profileDriver || null);
          loaded = true;
        }
      } catch (err) {
        console.warn("Dedicated drivers endpoint unavailable, using cabs list.", err?.message);
      }

      // 2. fallback: build the list from /cabs
      if (!loaded) {
        const res = await axios.get(API, authConfig);
        const cabsList = Array.isArray(res.data)
          ? res.data
          : res.data?.data || res.data?.cabs || [];
        setDrivers(cabsList.map(normalizeCab));
        setProfileDriver(null);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
      showNotice("error", errMessage(error, "Could not load drivers."));
    } finally {
      setLoading(false);
    }
  }, [authConfig, showNotice]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  /* ---------- FILTER + PAGINATION ---------- */
  const filteredDrivers = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return drivers.filter((item) => {
      const matchesQuery =
        !q ||
        (item.driverName || "").toLowerCase().includes(q) ||
        (item.driverPhone || "").toLowerCase().includes(q) ||
        (item.driverLicenseNumber || "").toLowerCase().includes(q) ||
        (item.vehicleNumber || "").toLowerCase().includes(q) ||
        (item.baseLocation || "").toLowerCase().includes(q);

      if (!matchesQuery) return false;

      if (filterType === "assigned") return hasDriver(item);
      if (filterType === "unassigned") return !hasDriver(item);
      if (filterType === "40km") {
        return hasDriver(item) && (item.serviceRadiusKm || 40) >= 40;
      }
      return true;
    });
  }, [drivers, searchQuery, filterType]);

  const totalPages = Math.max(1, Math.ceil(filteredDrivers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pagedDrivers = filteredDrivers.slice(pageStart, pageStart + PAGE_SIZE);

  const applyFilter = (value) => {
    setFilterType(value);
    setPage(1);
  };

  const assignedCount = drivers.filter(hasDriver).length;
  const hubCount = drivers.filter(
    (d) => hasDriver(d) && (d.serviceRadiusKm || 40) >= 40
  ).length;

  /* ---------- FORM ---------- */
  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingExisting(false);
    setForm(EMPTY_FORM);
    setErrors({});
    window.scrollTo({ top: 0 });
  };

  const openEdit = (driver) => {
    setEditingExisting(hasDriver(driver));
    setForm({
      cabId: driver.cabId || driver._id,
      driverName: driver.driverName || "",
      driverPhone: driver.driverPhone || "",
      driverAlternatePhone: driver.driverAlternatePhone || "",
      driverLicenseNumber: driver.driverLicenseNumber || "",
      experienceYears: driver.experienceYears ? String(driver.experienceYears) : "",
      driverPhoto: driver.driverPhoto || "",
    });
    setErrors({});
    setShowForm(true);
    window.scrollTo({ top: 0 });
  };

  const openAdd = () => {
    if (!drivers.length) {
      showNotice(
        "error",
        "Add at least one vehicle in the Cabs section before assigning a driver."
      );
      return;
    }
    const firstFree = drivers.find((d) => !hasDriver(d));
    setEditingExisting(false);
    setForm({ ...EMPTY_FORM, cabId: firstFree ? firstFree.cabId || firstFree._id : "" });
    setErrors({});
    setShowForm(true);
    window.scrollTo({ top: 0 });
  };

  const selectedCab = useMemo(
    () => drivers.find((d) => (d.cabId || d._id) === form.cabId) || null,
    [drivers, form.cabId]
  );

  const validate = () => {
    const err = {};
    if (!form.cabId) err.cabId = "Select a vehicle";
    if (!form.driverName.trim()) err.driverName = "Driver name is required";

    const phoneDigits = digitsOnly(form.driverPhone).length;
    if (!form.driverPhone.trim()) err.driverPhone = "Driver mobile number is required";
    else if (phoneDigits < 10 || phoneDigits > 13) err.driverPhone = "Enter a valid mobile number";

    if (form.driverAlternatePhone.trim()) {
      const altDigits = digitsOnly(form.driverAlternatePhone).length;
      if (altDigits < 10 || altDigits > 13) {
        err.driverAlternatePhone = "Enter a valid alternate number";
      }
    }

    if (!form.driverLicenseNumber.trim()) {
      err.driverLicenseNumber = "Licence number is required";
    }

    if (form.experienceYears !== "") {
      const exp = Number(form.experienceYears);
      if (!Number.isFinite(exp) || exp < 0 || exp > 60) {
        err.experienceYears = "Enter years between 0 and 60";
      }
    }

    setErrors(err);
    return err;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const err = validate();
    if (Object.keys(err).length) {
      setTimeout(() => {
        document
          .querySelector(".vcd-has-error")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      showNotice("error", "Please fix the highlighted fields before saving.");
      return;
    }

    const experience = Number(form.experienceYears) || 0;
    const payload = {
      driverName: form.driverName.trim(),
      driverPhone: form.driverPhone.trim(),
      driverAlternatePhone: form.driverAlternatePhone.trim(),
      driverLicenseNumber: form.driverLicenseNumber.trim(),
      experienceYears: experience,
      driverExperience: experience,
    };
    if (form.driverPhoto) payload.driverPhoto = form.driverPhoto;

    setSaving(true);
    try {
      try {
        await axios.put(`${API}/drivers/${form.cabId}`, payload, authConfig);
      } catch (err1) {
        // only fall back when the dedicated endpoint does not exist
        if (err1?.response?.status !== 404) throw err1;
        await axios.put(
          `${API}/${form.cabId}`,
          {
            driver: {
              name: payload.driverName,
              phone: payload.driverPhone,
              alternatePhone: payload.driverAlternatePhone,
              licenseNumber: payload.driverLicenseNumber,
              experienceYears: experience,
              ...(form.driverPhoto ? { photo: form.driverPhoto } : {}),
            },
            ...payload,
          },
          authConfig
        );
      }

      const wasEdit = editingExisting;
      closeForm();
      await fetchDrivers();
      showNotice("success", wasEdit ? "Driver details updated." : "Driver assigned to cab.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to update driver:", error);
      showNotice("error", errMessage(error, "Failed to save driver details."));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  /* ---------- RENDER HELPERS ---------- */
  const noticeBar = notice && (
    <div className={`vcd-notice ${notice.type}`} role="status">
      <span>{notice.text}</span>
      <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss">
        ✕
      </button>
    </div>
  );

  const renderPagination = () => {
    if (filteredDrivers.length <= PAGE_SIZE) return null;
    const from = pageStart + 1;
    const to = Math.min(pageStart + PAGE_SIZE, filteredDrivers.length);
    return (
      <div className="vcd-pagination">
        <span>
          Showing {from}–{to} of {filteredDrivers.length} records
        </span>
        <div className="vcd-pagination-btns">
          <button
            type="button"
            className="vcd-btn-cancel"
            disabled={safePage <= 1}
            onClick={() => setPage(safePage - 1)}
          >
            ‹ Prev
          </button>
          <span className="vcd-page-indicator">
            Page {safePage} of {totalPages}
          </span>
          <button
            type="button"
            className="vcd-btn-cancel"
            disabled={safePage >= totalPages}
            onClick={() => setPage(safePage + 1)}
          >
            Next ›
          </button>
        </div>
      </div>
    );
  };

  /* =========================================================
     FULL PAGE FORM
  ========================================================= */
  if (showForm) {
    const replacing =
      !editingExisting && selectedCab && hasDriver(selectedCab);

    return (
      <div className="vcd-container">
        {noticeBar}

        <div className="vcd-form-page">
          <div className="vcd-form-page-head">
            <h2>
              <MdPerson style={{ color: "#2563eb" }} />
              {editingExisting ? "Edit Driver Details" : "Assign Driver to Cab"}
            </h2>
            <button
              type="button"
              className="vcd-btn-cancel"
              onClick={closeForm}
              disabled={saving}
            >
              ‹ Back to list
            </button>
          </div>

          <form onSubmit={submit} noValidate>
            <div className="vcd-form-page-body">
              {/* VEHICLE */}
              <section className="vcd-form-section">
                <div className="vcd-form-section-head">
                  <h3>Vehicle</h3>
                  <p>Choose the cab this driver will operate.</p>
                </div>

                <Field label="Assigned Vehicle *" error={errors.cabId}>
                  <select
                    className="vcd-select"
                    value={form.cabId}
                    onChange={(e) => update("cabId", e.target.value)}
                    disabled={editingExisting}
                  >
                    <option value="">Select a vehicle</option>
                    {drivers.map((d) => (
                      <option key={d.cabId || d._id} value={d.cabId || d._id}>
                        {d.vehicleNumber} ({d.cabType} - {d.model || d.brand || "Cab"})
                        {hasDriver(d) ? ` — driver: ${d.driverName || d.driverPhone}` : " — no driver"}
                      </option>
                    ))}
                  </select>
                </Field>

                {selectedCab && (
                  <div className="vcd-vehicle-box">
                    <div className="vcd-vehicle-head">
                      <div className="vcd-inline-flex">
                        <MdDirectionsCar style={{ color: "#2563eb", fontSize: "18px" }} />
                        <strong>{selectedCab.vehicleNumber}</strong>
                      </div>
                      <span className="vcd-vehicle-type-tag">
                        {selectedCab.cabType}
                        {selectedCab.model ? ` · ${selectedCab.model}` : ""}
                      </span>
                    </div>
                    <div className="vcd-location-line">
                      <span className="vcd-inline-flex">
                        <MdLocationOn style={{ color: "#2563eb" }} />
                        {selectedCab.baseLocation}
                      </span>
                      <span className="vcd-badge-40km">
                        🎯 Within {selectedCab.serviceRadiusKm || 40} km
                      </span>
                    </div>
                  </div>
                )}

                {replacing && (
                  <div className="vcd-warning">
                    This cab already has a driver (
                    {selectedCab.driverName || selectedCab.driverPhone}). Saving will replace
                    the current driver.
                  </div>
                )}
              </section>

              {/* DRIVER */}
              <section className="vcd-form-section">
                <div className="vcd-form-section-head">
                  <h3>Driver details</h3>
                  <p>Contact and licence information used for dispatch.</p>
                </div>

                <Field label="Driver Full Name *" error={errors.driverName}>
                  <input
                    className="vcd-input"
                    placeholder="e.g. Ramesh Kumar"
                    value={form.driverName}
                    onChange={(e) => update("driverName", e.target.value)}
                  />
                </Field>

                <div className="vcd-form-row">
                  <Field label="Driver Phone Number *" error={errors.driverPhone}>
                    <input
                      className="vcd-input"
                      type="tel"
                      placeholder="e.g. +91 9876543210"
                      value={form.driverPhone}
                      onChange={(e) => update("driverPhone", e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Alternate Emergency Contact"
                    error={errors.driverAlternatePhone}
                  >
                    <input
                      className="vcd-input"
                      type="tel"
                      placeholder="e.g. +91 9123456780"
                      value={form.driverAlternatePhone}
                      onChange={(e) => update("driverAlternatePhone", e.target.value)}
                    />
                  </Field>
                </div>

                <div className="vcd-form-row">
                  <Field
                    label="Commercial Driving Licence Number *"
                    error={errors.driverLicenseNumber}
                  >
                    <input
                      className="vcd-input"
                      placeholder="e.g. DL-1420110012345"
                      value={form.driverLicenseNumber}
                      onChange={(e) =>
                        update("driverLicenseNumber", e.target.value.toUpperCase())
                      }
                    />
                  </Field>
                  <Field label="Experience (Years)" error={errors.experienceYears}>
                    <input
                      className="vcd-input"
                      type="number"
                      min="0"
                      max="60"
                      placeholder="e.g. 5"
                      value={form.experienceYears}
                      onChange={(e) => update("experienceYears", e.target.value)}
                    />
                  </Field>
                </div>
              </section>

              <div className="vcd-form-actions">
                <button
                  type="button"
                  className="vcd-btn-cancel"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="vcd-btn-submit" disabled={saving}>
                  {saving
                    ? "Saving Driver..."
                    : editingExisting
                    ? "Update Driver"
                    : "Save Driver Details"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  /* =========================================================
     MAIN LIST PAGE
  ========================================================= */
  return (
    <div className="vcd-container">
      {/* HEADER */}
      <header className="vcd-header">
        <div className="vcd-title-wrap">
          <h1>
            <span className="vcd-title-emoji">👨‍✈️</span>
            Cab Driver Details & Fleet Assignments
            <span className="vcd-live-pill">
              <span className="vcd-live-dot" /> Backend Verified
            </span>
          </h1>
          <p>
            Directory of commercial cab drivers, licence details, vehicle assignments, and 40 km dispatch readiness.
          </p>
        </div>

        <div className="vcd-header-actions">
          <button
            type="button"
            className="vcd-btn-refresh"
            onClick={fetchDrivers}
            title="Refresh drivers"
            aria-label="Refresh drivers"
          >
            <MdRefresh />
          </button>
          <button type="button" className="vcd-btn-add" onClick={openAdd}>
            <MdAdd style={{ fontSize: "20px" }} />
            Assign Driver to Cab
          </button>
        </div>
      </header>

      {noticeBar}

      {/* KPI CARDS */}
      <section className="vcd-stats-grid">
        <div
          className={`vcd-stat-card ${filterType === "all" ? "vcd-kpi-active" : ""}`}
          onClick={() => applyFilter("all")}
        >
          <div className="vcd-stat-info">
            <small>Total Fleet Drivers</small>
            <strong>{drivers.length}</strong>
            <span>Active vehicle rosters</span>
          </div>
          <div className="vcd-stat-icon-wrap vcd-stat-icon-blue">
            <MdPerson />
          </div>
        </div>

        <div
          className={`vcd-stat-card ${filterType === "assigned" ? "vcd-kpi-active" : ""}`}
          onClick={() => applyFilter("assigned")}
        >
          <div className="vcd-stat-info">
            <small>Drivers Assigned</small>
            <strong style={{ color: "#10b981" }}>{assignedCount}</strong>
            <span>Ready for passenger dispatch</span>
          </div>
          <div className="vcd-stat-icon-wrap vcd-stat-icon-green">
            <MdCheckCircle />
          </div>
        </div>

        <div
          className={`vcd-stat-card ${filterType === "40km" ? "vcd-kpi-active" : ""}`}
          onClick={() => applyFilter("40km")}
        >
          <div className="vcd-stat-info">
            <small>40 KM Hub Active</small>
            <strong style={{ color: "#2563eb" }}>{hubCount}</strong>
            <span>Intra-city & airport pickup range</span>
          </div>
          <div className="vcd-stat-icon-wrap vcd-stat-icon-amber">
            <MdRadar />
          </div>
        </div>

        <div
          className={`vcd-stat-card ${filterType === "unassigned" ? "vcd-kpi-active" : ""}`}
          onClick={() => applyFilter("unassigned")}
        >
          <div className="vcd-stat-info">
            <small>Cabs Awaiting Drivers</small>
            <strong style={{ color: "#f59e0b" }}>{drivers.length - assignedCount}</strong>
            <span>Unassigned vehicles</span>
          </div>
          <div className="vcd-stat-icon-wrap vcd-stat-icon-purple">
            <MdDirectionsCar />
          </div>
        </div>
      </section>

      {/* OWNER DRIVER BANNER */}
      {profileDriver?.driverName && (
        <section className="vcd-owner-driver-banner">
          <div className="vcd-owner-avatar">⭐</div>
          <div className="vcd-owner-info">
            <h3>
              {profileDriver.driverName}
              <span className="vcd-owner-badge">Registered Owner-Driver</span>
            </h3>
            <p>
              📞 {profileDriver.driverPhone || "N/A"} · Licence:{" "}
              <strong>{profileDriver.driverLicenseNumber || "Verified"}</strong>
              {profileDriver.experienceYears
                ? ` · ${profileDriver.experienceYears} Years Exp`
                : ""}
            </p>
          </div>
        </section>
      )}

      {/* TOOLBAR */}
      <div className="vcd-toolbar">
        <div className="vcd-search-box">
          <MdSearch className="vcd-search-icon" />
          <input
            type="text"
            placeholder="Search by driver name, phone, licence number, or vehicle plate..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
          />
          {searchQuery && (
            <button
              type="button"
              className="vcd-search-clear"
              onClick={() => {
                setSearchQuery("");
                setPage(1);
              }}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="vcd-filter-pills">
          {FILTER_PILLS.map((pill) => (
            <button
              key={pill.id}
              type="button"
              className={`vcd-filter-pill ${filterType === pill.id ? "active" : ""}`}
              onClick={() => applyFilter(pill.id)}
            >
              {pill.label}
            </button>
          ))}
        </div>

        <div className="vcd-view-toggle">
          <button
            type="button"
            className={`vcd-view-btn ${viewMode === "table" ? "active" : ""}`}
            onClick={() => setViewMode("table")}
            title="Table view"
            aria-label="Table view"
          >
            <MdViewList />
          </button>
          <button
            type="button"
            className={`vcd-view-btn ${viewMode === "cards" ? "active" : ""}`}
            onClick={() => setViewMode("cards")}
            title="Card view"
            aria-label="Card view"
          >
            <MdGridView />
          </button>
        </div>
      </div>

      {loading && (
        <div className="vcd-loading-state">
          <div className="vcd-spinner" />
          <strong>Loading drivers...</strong>
        </div>
      )}

      {!loading && filteredDrivers.length === 0 && (
        <div className="vcd-empty-state">
          <div style={{ fontSize: "54px", marginBottom: "16px" }}>👨‍✈️</div>
          <h3>No Driver Records Found</h3>
          <p>
            {searchQuery || filterType !== "all"
              ? "No drivers match your current search or filter. Try clearing the filters."
              : 'No cabs in your fleet yet. Add a cab first, then use "Assign Driver to Cab".'}
          </p>
        </div>
      )}

      {/* TABLE VIEW */}
      {!loading && filteredDrivers.length > 0 && viewMode === "table" && (
        <div className="vcd-table-wrap">
          <table className="vcd-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Driver</th>
                <th>Contact</th>
                <th>Licence Number</th>
                <th>Experience</th>
                <th>Assigned Vehicle</th>
                <th>Location & 40km Hub</th>
                <th>Status</th>
                <th className="vcd-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedDrivers.map((driver, i) => {
                const assigned = hasDriver(driver);
                const waNumber = digitsOnly(driver.driverPhone);
                return (
                  <tr key={driver._id}>
                    <td>{pageStart + i + 1}</td>

                    <td>
                      <div className="vcd-cell-person">
                        <div className="vcd-thumb">
                          {driver.driverPhoto ? (
                            <img src={driver.driverPhoto} alt={driver.driverName} />
                          ) : (
                            <span>👨‍✈️</span>
                          )}
                        </div>
                        <div>
                          <strong className="vcd-cell-title">
                            {driver.driverName || "Unassigned"}
                            {assigned && (
                              <MdVerified style={{ color: "#2563eb", fontSize: "14px", marginLeft: 4 }} />
                            )}
                          </strong>
                          <small className="vcd-cell-sub">
                            {assigned ? "Commercial Driver" : "Needs Driver"}
                          </small>
                        </div>
                      </div>
                    </td>

                    <td>
                      {driver.driverPhone ? (
                        <div>
                          <a href={`tel:${driver.driverPhone}`} className="vcd-cell-link">
                            📞 {driver.driverPhone}
                          </a>
                          {driver.driverAlternatePhone && (
                            <small className="vcd-cell-sub">Alt: {driver.driverAlternatePhone}</small>
                          )}
                        </div>
                      ) : (
                        <span className="vcd-detail-muted">N/A</span>
                      )}
                    </td>

                    <td>
                      <strong>{driver.driverLicenseNumber || "Pending"}</strong>
                    </td>

                    <td>
                      {driver.experienceYears ? `${driver.experienceYears} Years` : "—"}
                    </td>

                    <td>
                      <strong className="vcd-cell-title">{driver.vehicleNumber}</strong>
                      <small className="vcd-cell-sub">
                        {driver.cabType}
                        {driver.model ? ` · ${driver.model}` : ""}
                      </small>
                    </td>

                    <td>
                      <span className="vcd-cell-title">📍 {driver.baseLocation}</span>
                      <span className="vcd-badge-40km vcd-badge-inline">
                        Within {driver.serviceRadiusKm || 40} km
                      </span>
                    </td>

                    <td>
                      {assigned ? (
                        <span className="vcd-badge-assigned">🟢 Active</span>
                      ) : (
                        <span className="vcd-badge-unassigned">⚠️ Open</span>
                      )}
                    </td>

                    <td>
                      <div className="vcd-row-actions">
                        {driver.driverPhone && (
                          <a
                            href={`tel:${driver.driverPhone}`}
                            className="vcd-table-btn"
                            title="Call driver"
                          >
                            <MdPhone />
                          </a>
                        )}
                        {waNumber && (
                          <a
                            href={`https://wa.me/${waNumber}?text=${encodeURIComponent(
                              `Hello ${driver.driverName || "Driver"}, notice regarding cab ${driver.vehicleNumber} (${driver.cabType}).`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="vcd-table-btn wa"
                            title="WhatsApp"
                          >
                            <MdSend />
                          </a>
                        )}
                        <button
                          type="button"
                          className="vcd-table-btn edit"
                          onClick={() => openEdit(driver)}
                          title={assigned ? "Edit driver" : "Assign driver"}
                        >
                          <MdEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* CARD VIEW */}
      {!loading && filteredDrivers.length > 0 && viewMode === "cards" && (
        <div className="vcd-cards-grid">
          {pagedDrivers.map((driver) => {
            const assigned = hasDriver(driver);
            const waNumber = digitsOnly(driver.driverPhone);
            return (
              <article key={driver._id} className="vcd-card">
                <div className="vcd-card-head">
                  <div className="vcd-card-avatar">
                    {driver.driverPhoto ? (
                      <img
                        src={driver.driverPhoto}
                        alt={driver.driverName}
                        style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                      />
                    ) : (
                      "👨‍✈️"
                    )}
                  </div>

                  <div className="vcd-card-head-meta">
                    <h4 className="vcd-card-driver-name">
                      {driver.driverName || "Driver Unassigned"}
                      {assigned && <MdVerified style={{ color: "#2563eb", fontSize: "16px" }} />}
                    </h4>
                    {assigned ? (
                      <span className="vcd-badge-assigned">🟢 Assigned & Ready</span>
                    ) : (
                      <span className="vcd-badge-unassigned">⚠️ Awaiting Driver</span>
                    )}
                  </div>
                </div>

                <div className="vcd-card-body">
                  <div className="vcd-details-box">
                    <div className="vcd-detail-row">
                      <span className="vcd-detail-label">Mobile:</span>
                      {driver.driverPhone ? (
                        <a href={`tel:${driver.driverPhone}`} className="vcd-detail-link">
                          📞 {driver.driverPhone}
                        </a>
                      ) : (
                        <span className="vcd-detail-muted">Not provided</span>
                      )}
                    </div>

                    {driver.driverAlternatePhone && (
                      <div className="vcd-detail-row">
                        <span className="vcd-detail-label">Alternate:</span>
                        <span className="vcd-detail-val">📞 {driver.driverAlternatePhone}</span>
                      </div>
                    )}

                    <div className="vcd-detail-row">
                      <span className="vcd-detail-label">Licence No:</span>
                      <strong className="vcd-detail-val">
                        {driver.driverLicenseNumber || "Pending"}
                      </strong>
                    </div>

                    <div className="vcd-detail-row">
                      <span className="vcd-detail-label">Experience:</span>
                      <span className="vcd-detail-val">
                        {driver.experienceYears ? `${driver.experienceYears} Years Commercial` : "—"}
                      </span>
                    </div>
                  </div>

                  <div className="vcd-vehicle-box">
                    <div className="vcd-vehicle-head">
                      <div className="vcd-inline-flex">
                        <MdDirectionsCar style={{ color: "#2563eb", fontSize: "18px" }} />
                        <strong>{driver.vehicleNumber}</strong>
                      </div>
                      <span className="vcd-vehicle-type-tag">
                        {driver.cabType}
                        {driver.model ? ` · ${driver.model}` : ""}
                      </span>
                    </div>

                    <div className="vcd-location-line">
                      <span className="vcd-inline-flex">
                        <MdLocationOn style={{ color: "#2563eb" }} />
                        {driver.baseLocation}
                      </span>
                      <span className="vcd-badge-40km">
                        🎯 Within {driver.serviceRadiusKm || 40} km
                      </span>
                    </div>
                  </div>

                  <div className="vcd-card-footer">
                    {driver.driverPhone ? (
                      <div className="vcd-contact-actions">
                        <a href={`tel:${driver.driverPhone}`} className="vcd-btn-call" title="Call driver">
                          <MdPhone /> Call
                        </a>
                        {waNumber && (
                          <a
                            href={`https://wa.me/${waNumber}?text=${encodeURIComponent(
                              `Hello ${driver.driverName || "Driver"}, notice regarding cab ${driver.vehicleNumber} (${driver.cabType}).`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="vcd-btn-whatsapp"
                            title="Send WhatsApp message"
                          >
                            <MdSend /> WhatsApp
                          </a>
                        )}
                      </div>
                    ) : (
                      <span className="vcd-detail-muted">Attach phone for dispatch</span>
                    )}

                    <button
                      type="button"
                      className="vcd-btn-edit"
                      onClick={() => openEdit(driver)}
                      title="Edit driver details"
                    >
                      <MdEdit /> {assigned ? "Edit" : "Assign"}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {!loading && renderPagination()}
    </div>
  );
}